import {
    Add as AddIcon,
    Delete as DeleteIcon,
    Refresh as RefreshIcon,
    Search as SearchIcon,
    TrendingDown,
    Remove as TrendingFlat,
    TrendingUp,
} from '@mui/icons-material';
import {
    Autocomplete,
    Box,
    Button,
    Chip,
    CircularProgress,
    IconButton,
    List,
    ListItem,
    Paper,
    TextField,
    Typography
} from '@mui/material';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Sparkline } from '../../../components/Sparkline.js';
import { polygonApi, StockSearchResult } from '../../../services/polygon.js';
import { getMarketSnapshots } from '../../../services/profile.js';
import { useWidgetData } from '../hooks/useWidgetData.js';
import { WidgetProps } from '../types/index.js';

interface MarketDataItem {
  symbol: string;
  name: string;
  price: number;
  change: number;
  changePercent: number;
  volume: number;
  marketCap?: number;
  recentCloses?: number[];
}

// Custom debounce function
function debounce<T extends (...args: any[]) => any>(
  func: T,
  delay: number
): (...args: Parameters<T>) => void {
  let timeoutId: number;
  return (...args: Parameters<T>) => {
    clearTimeout(timeoutId);
    timeoutId = setTimeout(() => func(...args), delay) as any;
  };
}

export const MarketDataWidget: React.FC<WidgetProps> = ({
  widget,
  onUpdateOutputs,
  onUpdateState,
  onUpdateConfig,
}) => {
  const [selectedSymbol, setSelectedSymbol] = useState<string | null>(null);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchOptions, setSearchOptions] = useState<StockSearchResult[]>([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchInputValue, setSearchInputValue] = useState('');

  // Get symbols from config, default to popular stocks
  const symbols = widget.config.symbols || ['AAPL', 'GOOGL', 'MSFT', 'AMZN', 'TSLA'];

  // Fetch function for the hook
  const fetchMarketData = useCallback(async () => {
    if (symbols.length === 0) {
      return [];
    }
    
    // Use backend profile snapshots for intraday price, percent, and sparkline
    const [snapshots, [quotes, companies]] = await Promise.all([
      getMarketSnapshots(symbols, 60),
      Promise.all([
        polygonApi.getBatchStockQuotes(symbols),
        polygonApi.getBatchCompanyInfo(symbols),
      ]),
    ]);

    const quoteBySymbol = new Map(quotes.map(q => [q.symbol.toUpperCase(), q]));
    const companyBySymbol = new Map(companies.map(c => [c.symbol.toUpperCase(), c]));

    const formattedData: MarketDataItem[] = symbols.map((sym: string) => {
      const key = sym.toUpperCase();
      const snap = snapshots[key];
      const quote = quoteBySymbol.get(key);
      const co = companyBySymbol.get(key);

      const price = snap?.price ?? quote?.lastPrice ?? 0;
      const changePercent = snap?.changePercent ?? 0;
      const change = price * (changePercent / 100);
      const closes = Array.isArray(snap?.sparkline) ? (snap!.sparkline as number[]) : [];

      return {
        symbol: key,
        name: co?.name || key,
        price,
        change,
        changePercent,
        volume: quote?.volume || 0,
        marketCap: co?.marketCap,
        recentCloses: closes,
      } as MarketDataItem;
    }).filter((item: MarketDataItem) => item.price > 0 || item.volume > 0);

    return formattedData;
  }, [symbols.join(',')]);

  // Use the widget data hook for all data management
  const { data: marketData = [], loading, error, refresh } = useWidgetData<MarketDataItem[]>({
    widget,
    onUpdateState,
    fetchData: fetchMarketData,
    refreshInterval: 120000, // 2 minutes
    initialData: [],
  });

  // Refresh data when symbols change
  useEffect(() => {
    refresh();
  }, [symbols.join(',')]); // eslint-disable-line react-hooks/exhaustive-deps
  
  // Auto-select first symbol if none selected and we have market data
  useEffect(() => {
    if (!selectedSymbol && marketData.length > 0) {
      const firstSymbol = marketData[0].symbol;
      console.log('MarketDataWidget - Auto-selecting first symbol:', firstSymbol);
      setSelectedSymbol(firstSymbol);
    }
  }, [marketData.length]); // Only depend on data length to avoid loops

  // Update outputs when selection changes - using ref to track last output
  const lastOutputRef = useRef<any>(null);
  
  useEffect(() => {
    let selectedOutput = null;
    
    if (selectedSymbol && marketData.length > 0) {
      const selected = marketData.find(item => item.symbol === selectedSymbol);
      if (selected) {
        selectedOutput = selected;
      }
    }
    
    // Output both selected stock AND all stock data
    // Make sure outputs match the schema defined in widgetRegistry
    const outputs = {
      selectedStock: selectedOutput,
      stockData: marketData.length > 0 ? marketData : null
    };
    
    // Only update if output actually changed
    if (JSON.stringify(outputs) !== JSON.stringify(lastOutputRef.current)) {
      lastOutputRef.current = outputs;
      console.log('MarketDataWidget - Outputting:', {
        selectedStock: selectedOutput ? `${selectedOutput.symbol} selected` : 'none',
        stockDataCount: marketData.length
      });
      onUpdateOutputs(outputs);
    }
  }, [selectedSymbol, marketData, onUpdateOutputs]); // Include onUpdateOutputs in deps


  // Handle stock selection
  const handleSelectStock = (symbol: string | null) => {
    setSelectedSymbol(symbol);
  };

  // Debounced search function
  const debouncedSearch = useCallback(
    debounce(async (query: string) => {
      if (query.length < 2) {
        setSearchOptions([]);
        setSearchLoading(false);
        return;
      }

      try {
        setSearchLoading(true);
        const results = await polygonApi.searchStocks(query, 10);
        setSearchOptions(results);
      } catch (error) {
        console.error('Stock search error:', error);
        setSearchOptions([]);
      } finally {
        setSearchLoading(false);
      }
    }, 300),
    []
  );

  // Effect for search when input changes
  useEffect(() => {
    if (searchInputValue && searchInputValue.length >= 2) {
      debouncedSearch(searchInputValue);
    } else {
      setSearchOptions([]);
      setSearchLoading(false);
    }
  }, [searchInputValue, debouncedSearch]);

  // Add a new stock to the watchlist
  const handleAddStock = (_event: any, value: StockSearchResult | null) => {
    if (value && !symbols.includes(value.symbol)) {
      const newSymbols = [...symbols, value.symbol];
      onUpdateConfig({ symbols: newSymbols });
      setSearchInputValue('');
      setSearchOpen(false);
    }
  };

  // Remove a stock from the watchlist
  const handleRemoveStock = (symbolToRemove: string) => {
    const newSymbols = symbols.filter((s: string) => s !== symbolToRemove);
    onUpdateConfig({ symbols: newSymbols });
    if (selectedSymbol === symbolToRemove) {
      setSelectedSymbol(null);
    }
  };


  const getTrendIcon = (change: number) => {
    if (change > 0) return <TrendingUp />;
    if (change < 0) return <TrendingDown />;
    return <TrendingFlat />;
  };

  const formatVolume = (volume: number) => {
    if (volume >= 1e9) return `${(volume / 1e9).toFixed(1)}B`;
    if (volume >= 1e6) return `${(volume / 1e6).toFixed(1)}M`;
    if (volume >= 1e3) return `${(volume / 1e3).toFixed(1)}K`;
    return volume.toString();
  };

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Header with Refresh Button */}
      <Box sx={{ 
        p: 1, 
        borderBottom: 1, 
        borderColor: 'divider',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between'
      }}>
        <Typography variant="subtitle2" color="text.secondary">
          Live Market Data {loading && '(Loading...)'}
        </Typography>
        <Box sx={{ display: 'flex', gap: 0.5 }}>
          <IconButton size="small" onClick={() => setSearchOpen(true)} title="Add Stock">
            <AddIcon fontSize="small" />
          </IconButton>
          <IconButton size="small" onClick={refresh} title="Refresh">
            <RefreshIcon fontSize="small" />
          </IconButton>
        </Box>
      </Box>

      {/* Stock Search */}
      {searchOpen && (
        <Paper sx={(theme) => ({
          p: 1.5, m: 1, borderRadius: 2,
          background: `linear-gradient(180deg, rgba(20,20,20,0.6) 0%, rgba(10,10,10,0.6) 100%)`,
          backdropFilter: 'blur(8px) saturate(140%)',
          border: `1px solid rgba(255,255,255,0.08)`,
        })} elevation={0}>
          <Autocomplete
            size="small"
            open={searchOpen}
            onClose={() => setSearchOpen(false)}
            options={searchOptions}
            loading={searchLoading}
            getOptionLabel={(option) => `${option.symbol} - ${option.name}`}
            isOptionEqualToValue={(option, value) => option.symbol === value.symbol}
            onInputChange={(_event, newInputValue) => {
              setSearchInputValue(newInputValue);
            }}
            onChange={handleAddStock}
            filterOptions={(x) => x}
            renderOption={(props, option) => (
              <Box component="li" {...props}>
                <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                  <Box sx={{ flexGrow: 1 }}>
                    <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                      {option.symbol}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {option.name}
                    </Typography>
                  </Box>
                  <Chip
                    label={option.market}
                    size="small"
                    variant="outlined"
                    sx={{ height: '20px', fontSize: '0.7rem' }}
                  />
                </Box>
              </Box>
            )}
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder="Search stocks..."
                variant="outlined"
                autoFocus
                InputProps={{
                  ...params.InputProps,
                  startAdornment: (
                    <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} fontSize="small" />
                  ),
                  endAdornment: (
                    <>
                      {searchLoading ? <CircularProgress color="inherit" size={16} /> : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
            noOptionsText={
              searchInputValue.length < 2 
                ? "Type at least 2 characters to search"
                : searchLoading 
                ? "Searching..."
                : "No stocks found"
            }
          />
        </Paper>
      )}

      {/* Market Data List */}
      <Box sx={{ flex: 1, overflow: 'auto' }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
            <CircularProgress size={24} />
          </Box>
        ) : error ? (
          <Box sx={{ p: 2 }}>
            <Typography color="error" variant="body2">
              {error}
            </Typography>
          </Box>
        ) : symbols.length === 0 ? (
          <Box sx={{ p: 2, textAlign: 'center' }}>
            <Typography color="text.secondary" variant="body2" gutterBottom>
              No stocks in watchlist
            </Typography>
            <Button
              startIcon={<AddIcon />}
              size="small"
              onClick={() => setSearchOpen(true)}
              variant="outlined"
            >
              Add Stock
            </Button>
          </Box>
        ) : marketData.length === 0 ? (
          <Box sx={{ p: 2 }}>
            <Typography color="text.secondary" variant="body2">
              Loading market data...
            </Typography>
          </Box>
        ) : (
          <List dense>
            {marketData.map((item) => (
              <ListItem
                key={item.symbol}
                onClick={() => handleSelectStock(item.symbol)}
                secondaryAction={
                  <IconButton 
                    edge="end" 
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleRemoveStock(item.symbol);
                    }}
                    sx={{ 
                      opacity: 0,
                      transition: 'opacity 0.2s',
                      '.MuiListItem-root:hover &': {
                        opacity: 1,
                      },
                    }}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                }
                sx={{
                  cursor: 'pointer',
                  backgroundColor: selectedSymbol === item.symbol ? 'action.selected' : 'transparent',
                  borderRadius: 1,
                  mb: 0.5,
                  mx: 0.5,
                  pr: 6,
                  '&:hover': {
                    backgroundColor: 'action.hover',
                  },
                }}
              >
                <Box sx={{ width: '100%' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Typography variant="subtitle2">{item.symbol}</Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="subtitle2">${item.price.toFixed(2)}</Typography>
                      <Sparkline data={item.recentCloses || []} color={item.change > 0 ? '#34C759' : item.change < 0 ? '#FF3B30' : '#8B66FF'} width={100} height={24} />
                    </Box>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 0.5 }}>
                    <Typography variant="caption" color="text.secondary" noWrap sx={{ flex: 1 }}>
                      {item.name}
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Chip
                        icon={getTrendIcon(item.change)}
                        label={`${item.changePercent.toFixed(2)}%`}
                        size="small"
                        color={item.change > 0 ? 'success' : item.change < 0 ? 'error' : 'default'}
                        sx={{ height: 20 }}
                      />
                      <Typography variant="caption" color="text.secondary">
                        Vol: {formatVolume(item.volume)}
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              </ListItem>
            ))}
          </List>
        )}
      </Box>
    </Box>
  );
};